/**
 * Tide tokens - the ONE place category and status colours are assigned to keys
 * (docs/design/tide.md). Values are CSS custom properties defined in
 * src/styles/tide/tide.css, so a mark, its legend swatch and its pill always
 * resolve to the same colour. Each category also carries a non-colour marker so
 * meaning survives grayscale, colour-vision differences and forced colours.
 *
 * Monday board status colours delivered by the backend are NOT here and must
 * never be remapped through these tokens.
 */

/**
 * Non-colour identifier drawn next to every category colour. The six shapes are
 * the contract's (domains, origins, aging, status). `glyph` is a short text
 * code in a small outlined badge, used for status words: there are more words
 * than shapes, and a code is unique by construction and never one of the
 * domain or origin shapes.
 */
export type MarkerShape = 'square' | 'circle' | 'triangle' | 'diamond' | 'dash' | 'hatch' | 'glyph';

export interface CategoryToken { color: string; marker: MarkerShape; label: string }

export type DomainKey = 'PA' | 'MANIFEST' | 'UMED' | 'FINANCE' | 'COVERAGE';
export type OriginKey = 'PERSON' | 'SYSTEM' | 'IMPORT';
export type StatusKey = 'danger' | 'caution' | 'ok' | 'missing' | 'withheld';

export const DOMAIN: Record<DomainKey, CategoryToken> = {
  PA: { color: 'var(--tide-cat-pa)', marker: 'circle', label: 'PA' },
  MANIFEST: { color: 'var(--tide-cat-manifest)', marker: 'square', label: 'Manifest' },
  UMED: { color: 'var(--tide-cat-umed)', marker: 'diamond', label: 'UMED' },
  FINANCE: { color: 'var(--tide-cat-finance)', marker: 'triangle', label: 'Finance' },
  COVERAGE: { color: 'var(--tide-cat-coverage)', marker: 'dash', label: 'Coverage' },
};

export const ORIGIN: Record<OriginKey, CategoryToken> = {
  PERSON: { color: 'var(--tide-cat-person)', marker: 'circle', label: 'People' },
  SYSTEM: { color: 'var(--tide-cat-system)', marker: 'diamond', label: 'Automation' },
  IMPORT: { color: 'var(--tide-cat-import)', marker: 'hatch', label: 'Imports' },
};

export const STATUS: Record<StatusKey, { fg: string; bg: string; label: string }> = {
  danger: { fg: 'var(--tide-status-danger)', bg: 'var(--tide-status-danger-bg)', label: 'Needs attention' },
  caution: { fg: 'var(--tide-status-caution)', bg: 'var(--tide-status-caution-bg)', label: 'Caution' },
  ok: { fg: 'var(--tide-status-ok)', bg: 'var(--tide-status-ok-bg)', label: 'On track' },
  missing: { fg: 'var(--tide-status-missing)', bg: 'var(--tide-status-missing-bg)', label: 'Not recorded' },
  withheld: { fg: 'var(--tide-status-withheld)', bg: 'var(--tide-status-withheld-bg)', label: 'Withheld' },
};

/** The non-colour mark for each status, shared by pills, tiles and state blocks. */
export const STATUS_MARKER: Record<StatusKey, MarkerShape> = {
  danger: 'triangle', caution: 'diamond', ok: 'circle', missing: 'dash', withheld: 'hatch',
};

/** Time in the current stage: older is redder. Unknown is grey and hatched, because unknown is not young. */
export const AGE = {
  d0_1: { color: 'var(--tide-age-0)', marker: 'square', label: '0-1d' },
  d1_3: { color: 'var(--tide-age-1)', marker: 'square', label: '1-3d' },
  d3_7: { color: 'var(--tide-age-2)', marker: 'square', label: '3-7d' },
  d7_14: { color: 'var(--tide-age-3)', marker: 'square', label: '7-14d' },
  d14_30: { color: 'var(--tide-age-4)', marker: 'square', label: '14-30d' },
  d30p: { color: 'var(--tide-age-5)', marker: 'square', label: '30d+' },
  unknown: { color: 'var(--tide-age-unknown)', marker: 'hatch', label: 'Age unknown' },
} as const satisfies Record<string, CategoryToken>;

/** Statistics drawn together (median bar vs p90 line). */
export const STAT = {
  median: { color: 'var(--tide-stat-median)', marker: 'square', label: 'Median' },
  p90: { color: 'var(--tide-stat-tail)', marker: 'circle', label: 'p90' },
} as const satisfies Record<string, CategoryToken>;

/** Sequential stops for heat cells, 0 = no activity. */
export const SEQ = ['var(--tide-seq-0)', 'var(--tide-seq-1)', 'var(--tide-seq-2)', 'var(--tide-seq-3)', 'var(--tide-seq-4)'] as const;

/**
 * Status words (the words a PA case closes with, shown in "Cleanup exits per
 * day"). A FIXED map: every known word has its own colour and its own glyph,
 * so no two can be confused in one chart. The colours are a 4 x 4 grid - four
 * hue columns no domain or origin uses (leaf 113, green 136, sea 158, gold 48 -
 * the midpoint of the 59-degree gap between Manifest orange 18.7 and Finance
 * olive 77.8, so at least 29 degrees from each)
 * by four lightness steps (L* 26 / 37 / 48 / 59, every one at least 3.2:1 on
 * white) - and any two words differ by >= 20 degrees of hue or a full step of
 * lightness. The words that close most cases sit in different columns AND rows.
 *
 * The list is the PA vocabulary's terminal words
 * (backend/src/services/advocate/advocateNativeService.js TERMINAL_WORDS), plus
 * DUPLICATE, WITHDRAWN, ALREADY_ENROLLED, REVERSAL and ENROLLED. A word not in
 * it (a new board spelling) still gets a colour by hash, but its glyph is "?"
 * plus its first letter, so it can never share a (colour, glyph) pair with a
 * known word; it is also ordered after every known word.
 */
export interface WordToken { color: string; marker: 'glyph'; glyph: string; known: boolean }

const W = (cell: string, glyph: string) => ({ color: `var(--tide-word-${cell})`, glyph });

export const STATUS_WORD_TOKENS: Record<string, { color: string; glyph: string }> = {
  // the five that close most cases: different hue AND different lightness
  DNQ: W('green-2', 'DQ'),
  DUPLICATE: W('gold-3', 'DU'),
  DECEASED: W('sea-1', 'DE'),
  WITHDRAWN: W('leaf-4', 'WD'),
  ALREADY_ENROLLED: W('sea-4', 'AE'),
  // outcomes
  NITWL: W('leaf-2', 'NI'),
  'C-NITWL': W('leaf-1', 'CN'),
  // lifecycle
  DISCHARGED: W('sea-3', 'DS'),
  HOSPICE: W('green-3', 'HO'),
  'DROPPED FACILITY': W('gold-2', 'DF'),
  // other closes
  REVERSAL: W('gold-4', 'RV'),
  ENROLLED: W('green-4', 'EN'),
  // R1: back to the provider
  'REEVALUATE - CURRENT YEAR': W('sea-2', 'RC'),
  'REEVALUATE - 12+ MONTHS': W('green-1', 'R12'),
  'RESUBMIT TO PROVIDER': W('leaf-3', 'RP'),
  // D-29
  'NEEDS VERIFICATION': W('gold-1', 'NV'),
};

/** Known words in vocabulary order: stacks and legends list these first, unknown words after. */
export const KNOWN_STATUS_WORDS = Object.keys(STATUS_WORD_TOKENS);

/** Same normalisation as the backend's legendFor: case and whitespace only, never a guess. */
export const normalizeWord = (word: string) => String(word || '').trim().toUpperCase().replace(/\s+/g, ' ');

const WORD_CELLS = Array.from(new Set(Object.values(STATUS_WORD_TOKENS).map((t) => t.color)));

function hash(name: string): number {
  let h = 7;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h;
}

/** Colour + glyph for a status word: the fixed map, or the hashed fallback for an unknown word. */
export function wordToken(word: string): WordToken {
  const k = normalizeWord(word);
  const known = STATUS_WORD_TOKENS[k];
  if (known) return { ...known, marker: 'glyph', known: true };
  const first = (k.match(/[A-Z0-9]/) || ['?'])[0];
  return { color: WORD_CELLS[hash(k) % WORD_CELLS.length], marker: 'glyph', glyph: `?${first}`, known: false };
}

/** Sort key: known words in vocabulary order, then unknown words alphabetically. */
export function compareWords(a: string, b: string): number {
  const ia = KNOWN_STATUS_WORDS.indexOf(normalizeWord(a));
  const ib = KNOWN_STATUS_WORDS.indexOf(normalizeWord(b));
  if (ia >= 0 && ib >= 0) return ia - ib;
  if (ia >= 0) return -1;
  if (ib >= 0) return 1;
  return normalizeWord(a).localeCompare(normalizeWord(b));
}

/** @deprecated name kept for callers; use wordToken. */
export const seriesToken = wordToken;
